import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, LineChart, Badge } from '@tremor/react';
import { KPICard } from '@/components/KPICard';
import { getGolden5, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /golden5 — routers/golden5.py) ────────────────────

interface CasoRun {
  va: number | null;
  va_historico?: number | null; // [10/09] VA sobre valor histórico (sem deriva de data); null = caso sem gabarito histórico
  n_hist?: number | null;
  ts: string;
  bruto: number | null;
  n_match: number | null;
  n_verbas_perito: number | null;
  n_extras?: number;
  extras?: string[];
  status: string;
}

interface Run {
  run: string;
  inicio: string;
  fim: string;
  media: number | null;
  media_historico?: number | null;
  n_hist_casos?: number;
  n_ok: number;
  n_total: number;
  extras_total?: number;
  casos: Record<string, CasoRun>;
}

interface MoverLinha {
  verba: string;
  base: number;
  atual: number;
  anterior: number | null; // penúltima medição ok do caso (null = só 1 medição)
  pico: number;
  delta: number;
}

interface EstadoCaso {
  ts: string;
  va: number;
  va_historico?: number | null;
  n_hist?: number | null;
  va_anterior: number | null;
  ts_anterior: string | null;
  va_confirmado: number | null;
  n_match: number | null;
  n_verbas_perito: number | null;
  n_extras: number;
  extras: string[];
  bruto: number | null;
}

interface Estado {
  casos: Record<string, EstadoCaso>;
  va_ultima: number | null;
  va_confirmado: number | null;
  n_casos_confirmados: number;
  va_historico?: number | null;
  n_casos_historico?: number;
  extras_total: number;
}

interface Golden5Data {
  disponivel: boolean;
  erro?: string;
  atualizado_em: string;
  em_curso: { rodando: boolean; desde: string | null };
  nomes: Record<string, string>;
  runs: Run[];
  fase_b: { ts: string; caso: string; va: number }[];
  movers: Record<string, MoverLinha[]>;
  estado?: Estado;
}

// Ordem e cor FIXAS por caso (regra dataviz: cor segue a entidade, nunca o rank)
const CASOS = ['1001671', '1000904', '1000784', '1001271', '1002690'];
const CORES = ['blue', 'orange', 'emerald', 'amber', 'pink'];

const fmtVA = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toFixed(3).replace('.', ',');
const fmtPct = (v: number) => `${(v * 100).toFixed(1).replace('.', ',')}%`;

export function Golden5Page() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['golden5'],
    queryFn: () => getGolden5().then((r) => r.data as Golden5Data),
    refetchInterval: 5 * 60 * 1000, // rodadas duram horas; 5min é sobra
  });

  const errMsg = error
    ? extractErrorMessage(error, 'falha ao carregar a série golden5')
    : data && !data.disponivel
      ? (data.erro ?? 'indisponível')
      : null;

  const derived = useMemo(() => {
    if (!data?.disponivel || !data.runs.length) return null;
    const runs = data.runs;
    const completas = runs.filter((r) => r.n_ok === r.n_total && r.n_total >= 5);
    const baseline = runs[0];
    const ultima = completas.length ? completas[completas.length - 1] : runs[runs.length - 1];
    const pico = Math.max(...runs.map((r) => r.media ?? 0));
    const casadas = (run: Run) =>
      CASOS.reduce(
        (acc, c) => {
          const k = run.casos[c];
          if (k?.status === 'ok') {
            acc[0] += k.n_match ?? 0;
            acc[1] += k.n_verbas_perito ?? 0;
          }
          return acc;
        },
        [0, 0]
      );
    // slope: baseline → última rodada completa
    const slope = [
      Object.fromEntries([
        ['ponto', `baseline ${baseline.inicio.slice(5, 10).replace('-', '/')}`],
        ...CASOS.map((c) => [data.nomes[c] ?? c, baseline.casos[c]?.va ?? null]),
      ]),
      Object.fromEntries([
        ['ponto', `${ultima.run} · ${ultima.inicio.slice(5, 10).replace('-', '/')}`],
        ...CASOS.map((c) => [data.nomes[c] ?? c, ultima.casos[c]?.va ?? null]),
      ]),
    ];
    // série completa: fase-B (por data) + rodadas full-pipeline (por run)
    const faseBDates = [...new Set(data.fase_b.map((p) => p.ts.slice(0, 10)))].sort();
    const serie = [
      ...faseBDates.map((d) =>
        Object.fromEntries([
          ['r', `${d.slice(8, 10)}/${d.slice(5, 7)}*`],
          ...CASOS.map((c) => [
            data.nomes[c] ?? c,
            data.fase_b.find((p) => p.caso === c && p.ts.startsWith(d))?.va ?? null,
          ]),
        ])
      ),
      ...runs.map((r) =>
        Object.fromEntries([
          ['r', r.run],
          ...CASOS.map((c) => [data.nomes[c] ?? c, r.casos[c]?.va ?? null]),
        ])
      ),
    ];
    // movers agregados (todas as verbas de todos os casos, baseline → última)
    const todosMovers = Object.entries(data.movers).flatMap(([caso, linhas]) =>
      linhas.map((l) => ({ caso, ...l }))
    );
    // [09/09] CONFIRMADO = as duas últimas medições do caso concordam; com uma
    // só é PROVISÓRIO (a série re-extrai com LLM: 904 foi 0,53→0,88 em 24h).
    const ganhos = todosMovers
      .filter((l) => l.delta > 0.02)
      .map((l) => ({ ...l, confirmado: l.anterior !== null && l.anterior - l.base > 0.02 }))
      .sort((a, b) => Number(b.confirmado) - Number(a.confirmado) || b.delta - a.delta);
    // queda = última rodada bem abaixo do PICO da série (pega verba que subiu
    // e depois sumiu — o padrão da regressão DSR de 01/09, invisível no
    // baseline→última porque no baseline ela também era 0)
    const quedas = todosMovers
      .filter((l) => l.pico - l.atual > 0.1)
      .map((l) => ({ ...l, confirmado: l.anterior !== null && l.pico - l.anterior > 0.1 }))
      .sort((a, b) => Number(b.confirmado) - Number(a.confirmado) || (a.atual - a.pico) - (b.atual - b.pico));
    const estado = data.estado;
    const extrasAtual = estado
      ? CASOS.filter((c) => estado.casos[c]).map((c) => ({ caso: c, ...estado.casos[c] }))
      : [];
    return { runs, baseline, ultima, pico, casadas, slope, serie, ganhos, quedas, estado, extrasAtual };
  }, [data]);

  const categorias = CASOS.map((c) => data?.nomes[c] ?? c);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Title>Golden5 vs Perito</Title>
          <Text className="text-xs text-slate-400 max-w-3xl">
            Régua determinística full-pipeline (upload → extração fresca → PJe-Calc →
            cálculo) dos 5 casos golden com gabarito do perito. VA por verba = max(0, 1 −
            |nosso−perito|/perito); VA do caso = média simples (verba não entregue vale 0;
            verba EXTRA nossa não conta — por isso o contador de extras ao lado).
            "Confirmado" = as duas últimas medições do caso concordam; uma rodada só é
            provisória. "VA histórico" = mesma régua sobre o valor HISTÓRICO (coluna
            Diferença, antes de correção e juros) — tira a deriva de data de liquidação
            (784: perito 08/2024 × nós 09/2026 = ×1,227 em toda verba); só existe para
            casos com planilha PJe-Calc nos autos. Fonte: eval/series/va_semanal.jsonl,
            alimentado a cada deploy/flip pelo regua_on_deploy. n=5 — evidência
            direcional, não medida com IC.
          </Text>
        </div>
        {data?.em_curso.rodando && (
          <Badge color="amber">
            ⏳ rodada em curso{data.em_curso.desde ? ` desde ${data.em_curso.desde.slice(11, 16)}Z` : ''}
          </Badge>
        )}
      </div>

      <Grid numItems={2} numItemsLg={3} className="gap-4">
        <KPICard
          label="VA médio (última rodada completa)"
          value={derived ? fmtPct(derived.ultima.media ?? 0) : null}
          delta={
            derived && derived.ultima.media != null && derived.baseline.media != null
              ? derived.ultima.media - derived.baseline.media
              : null
          }
          hint={derived ? `${derived.ultima.run} · baseline ${fmtPct(derived.baseline.media ?? 0)}` : undefined}
          loading={isLoading}
          error={errMsg}
        />
        <KPICard
          label="VA histórico (última rodada completa)"
          value={derived?.ultima.media_historico != null ? fmtPct(derived.ultima.media_historico) : null}
          delta={
            derived && derived.ultima.media_historico != null && derived.ultima.media != null
              ? derived.ultima.media_historico - derived.ultima.media
              : null
          }
          hint={
            derived
              ? `valor histórico (sem deriva de data) · ${derived.ultima.n_hist_casos ?? 0}/5 casos com gabarito histórico · Δ vs VA da rodada`
              : undefined
          }
          loading={isLoading}
          error={errMsg}
        />
        <KPICard
          label="VA confirmado (2 medições)"
          value={derived?.estado?.va_confirmado != null ? fmtPct(derived.estado.va_confirmado) : null}
          hint={
            derived?.estado
              ? `média por caso de min(última, penúltima) · ${derived.estado.n_casos_confirmados}/5 casos com 2 medições`
              : undefined
          }
          loading={isLoading}
          error={errMsg}
        />
        <KPICard
          label="Verbas extras (última medição)"
          value={derived?.estado ? String(derived.estado.extras_total) : null}
          hint="entregues por nós e ausentes no perito — NÃO contam no VA (super-inclusão)"
          loading={isLoading}
          error={errMsg}
        />
        <KPICard
          label="Pico da série"
          value={derived ? fmtPct(derived.pico) : null}
          hint="melhor média entre todas as rodadas"
          loading={isLoading}
          error={errMsg}
        />
        <KPICard
          label="Verbas casadas (última)"
          value={
            derived
              ? `${derived.casadas(derived.ultima)[0]}/${derived.casadas(derived.ultima)[1]}`
              : null
          }
          hint={derived ? `baseline: ${derived.casadas(derived.baseline)[0]}/${derived.casadas(derived.baseline)[1]}` : undefined}
          loading={isLoading}
          error={errMsg}
        />
        <KPICard
          label="Rodadas medidas"
          value={derived ? String(derived.runs.length) : null}
          hint="full-pipeline desde o baseline 23/08"
          loading={isLoading}
          error={errMsg}
        />
      </Grid>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title>Slope — baseline → última rodada</Title>
          <Text className="text-xs text-slate-400">
            Mesmos casos, mesma régua, ambos full-pipeline. 1,000 = idêntico ao perito.
          </Text>
          {!isLoading && !errMsg && derived && (
            <LineChart
              className="mt-3 h-72"
              data={derived.slope}
              index="ponto"
              categories={categorias}
              colors={CORES}
              valueFormatter={fmtVA}
              minValue={0}
              maxValue={1}
              showAnimation
              connectNulls
            />
          )}
        </Card>
        <Card>
          <Title>Série completa por rodada</Title>
          <Text className="text-xs text-slate-400">
            Pontos com * (≤16/08) são Fase-B-only — extração congelada, NÃO comparáveis
            com as rodadas R1+ (full-pipeline).
          </Text>
          {!isLoading && !errMsg && derived && (
            <LineChart
              className="mt-3 h-72"
              data={derived.serie}
              index="r"
              categories={categorias}
              colors={CORES}
              valueFormatter={fmtVA}
              minValue={0}
              maxValue={1}
              showAnimation
              connectNulls
            />
          )}
        </Card>
      </Grid>

      <Card>
        <Title>Todas as rodadas</Title>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm text-left text-slate-300">
            <thead className="text-xs uppercase text-slate-400 border-b border-slate-700">
              <tr>
                <th className="py-2 pr-3">rodada</th>
                {CASOS.map((c) => (
                  <th key={c} className="py-2 px-3 text-right">{c}</th>
                ))}
                <th className="py-2 px-3 text-right">média</th>
                <th className="py-2 px-3 text-right" title="VA sobre valor histórico (coluna Diferença), média dos casos com gabarito histórico">hist</th>
                <th className="py-2 px-3 text-right">Δpp</th>
                <th className="py-2 px-3 text-right" title="verbas nossas sem par no perito (não contam no VA)">extras</th>
              </tr>
            </thead>
            <tbody>
              {derived?.runs.map((r, i) => {
                const prev = i > 0 ? derived.runs[i - 1] : null;
                const comparavel = prev && r.n_ok === r.n_total && prev.n_ok === prev.n_total;
                const dpp =
                  comparavel && r.media != null && prev?.media != null
                    ? (r.media - prev.media) * 100
                    : null;
                return (
                  <tr key={r.run} className="border-b border-slate-800">
                    <td className="py-1.5 pr-3 whitespace-nowrap">
                      <b>{r.run}</b>{' '}
                      <span className="text-slate-500">
                        {r.inicio.slice(5, 16).replace('-', '/').replace('T', ' ')}Z
                      </span>
                    </td>
                    {CASOS.map((c) => {
                      const k = r.casos[c];
                      return (
                        <td key={c} className="py-1.5 px-3 text-right tabular-nums">
                          {k?.status === 'ok' ? fmtVA(k.va) : (
                            <span className="text-red-400">{k ? 'falhou' : '—'}</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-1.5 px-3 text-right tabular-nums font-semibold text-slate-100">
                      {fmtVA(r.media)}{r.n_ok < r.n_total ? ` (n=${r.n_ok})` : ''}
                    </td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-sky-300">
                      {r.media_historico == null ? '—' : `${fmtVA(r.media_historico)} (n=${r.n_hist_casos ?? 0})`}
                    </td>
                    <td className={`py-1.5 px-3 text-right tabular-nums ${
                      dpp == null ? 'text-slate-500' : dpp > 0 ? 'text-emerald-400' : dpp < 0 ? 'text-red-400' : 'text-slate-500'
                    }`}>
                      {dpp == null ? '—' : `${dpp > 0 ? '+' : ''}${dpp.toFixed(1).replace('.', ',')}`}
                    </td>
                    <td className={`py-1.5 px-3 text-right tabular-nums ${
                      (r.extras_total ?? 0) > 0 ? 'text-amber-400' : 'text-slate-500'
                    }`}>
                      {r.extras_total ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title>Maiores ganhos por verba (baseline → última)</Title>
          <Text className="text-xs text-slate-400">
            Verba não entregue vale 0 — recuperar entrega vale mais que afinar valor.
          </Text>
          <div className="mt-3 space-y-1.5">
            {derived?.ganhos.slice(0, 10).map((l) => (
              <div key={`${l.caso}-${l.verba}`} className="flex justify-between gap-3 text-sm">
                <span className="text-slate-300 truncate">
                  <span className="text-slate-500">{l.caso}</span> · {l.verba}
                </span>
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <Badge size="xs" color={l.confirmado ? 'emerald' : 'slate'}>
                    {l.confirmado ? '✓ 2 medições' : 'provisório'}
                  </Badge>
                  <span className="tabular-nums text-emerald-400">
                    {fmtVA(l.base)} → {fmtVA(l.atual)}
                  </span>
                </span>
              </div>
            ))}
            {derived && !derived.ganhos.length && <Text>nenhum ganho relevante</Text>}
          </div>
        </Card>
        <Card>
          <Title>Maiores quedas por verba (pico da série → última)</Title>
          <Text className="text-xs text-slate-400">
            Verba que já entregou bem e caiu — candidata a regressão de flip/deploy.
          </Text>
          <div className="mt-3 space-y-1.5">
            {derived?.quedas.slice(0, 10).map((l) => (
              <div key={`${l.caso}-${l.verba}`} className="flex justify-between gap-3 text-sm">
                <span className="text-slate-300 truncate">
                  <span className="text-slate-500">{l.caso}</span> · {l.verba}
                </span>
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <Badge size="xs" color={l.confirmado ? 'red' : 'slate'}>
                    {l.confirmado ? '✓ 2 medições' : 'provisório'}
                  </Badge>
                  <span className="tabular-nums text-red-400">
                    pico {fmtVA(l.pico)} → {fmtVA(l.atual)}
                  </span>
                </span>
              </div>
            ))}
            {derived && !derived.quedas.length && <Text>nenhuma queda relevante</Text>}
          </div>
        </Card>
      </Grid>

      <Card>
        <Title>Verbas extras por caso (última medição) — o que o VA não vê</Title>
        <Text className="text-xs text-slate-400">
          Entregamos e o perito não calculou. A régua não desconta nada por isso; para o
          cliente é cálculo inflado. Um fix que sobe o VA inflando esta coluna não é ganho.
        </Text>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm text-left text-slate-300">
            <thead className="text-xs uppercase text-slate-400 border-b border-slate-700">
              <tr>
                <th className="py-2 pr-3">caso</th>
                <th className="py-2 px-3 text-right">VA última</th>
                <th className="py-2 px-3 text-right" title="VA sobre valor histórico (sem deriva de data de liquidação); — = caso sem planilha PJe-Calc nos autos">VA hist</th>
                <th className="py-2 px-3 text-right">VA penúltima</th>
                <th className="py-2 px-3 text-right">casadas</th>
                <th className="py-2 px-3 text-right">extras</th>
                <th className="py-2 px-3">quais</th>
              </tr>
            </thead>
            <tbody>
              {derived?.extrasAtual.map((e) => (
                <tr key={e.caso} className="border-b border-slate-800 align-top">
                  <td className="py-1.5 pr-3 whitespace-nowrap">
                    <b>{e.caso}</b>{' '}
                    <span className="text-slate-500">{e.ts.slice(5, 16).replace('-', '/').replace('T', ' ')}Z</span>
                  </td>
                  <td className="py-1.5 px-3 text-right tabular-nums">{fmtVA(e.va)}</td>
                  <td className="py-1.5 px-3 text-right tabular-nums text-sky-300">
                    {e.va_historico == null ? '—' : `${fmtVA(e.va_historico)} (${e.n_hist ?? 0}v)`}
                  </td>
                  <td className="py-1.5 px-3 text-right tabular-nums text-slate-400">
                    {e.va_anterior == null ? '—' : fmtVA(e.va_anterior)}
                  </td>
                  <td className="py-1.5 px-3 text-right tabular-nums">
                    {e.n_match ?? '—'}/{e.n_verbas_perito ?? '—'}
                  </td>
                  <td className={`py-1.5 px-3 text-right tabular-nums font-semibold ${
                    e.n_extras > 0 ? 'text-amber-400' : 'text-slate-500'
                  }`}>
                    {e.n_extras}
                  </td>
                  <td className="py-1.5 px-3 text-xs text-slate-400">
                    {e.extras.length ? e.extras.join(' · ') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
